import React, { useRef, useEffect, useCallback, useState } from 'react';

export default function RichEditor({ value, onChange, placeholder='Write a detailed product description…' }) {
  const editorRef  = useRef(null);
  const fileRef    = useRef(null);
  const isUpdating = useRef(false);
  const [showHTML, setShowHTML] = useState(false);
  const [htmlVal,  setHtmlVal]  = useState('');
  const [selImg,   setSelImg]   = useState(null); // selected image element
  const [imgSize,  setImgSize]  = useState({ w:0, h:0 });

  useEffect(() => {
    if (editorRef.current && !isUpdating.current) {
      const incoming = value || '';
      if (editorRef.current.innerHTML !== incoming && (editorRef.current.innerHTML === '' || incoming === '')) {
        editorRef.current.innerHTML = incoming;
      }
    }
  }, [value]);

  const handleInput = useCallback(() => {
    isUpdating.current = true;
    onChange(editorRef.current.innerHTML);
    setTimeout(() => { isUpdating.current = false; }, 0);
  }, [onChange]);

  const exec      = (cmd)  => { editorRef.current.focus(); document.execCommand(cmd, false, null); handleInput(); };
  const execBlock = (t)    => { editorRef.current.focus(); document.execCommand('formatBlock', false, t); handleInput(); };
  const execLink  = ()     => { const u=window.prompt('Enter URL:'); if(u){editorRef.current.focus();document.execCommand('createLink',false,u);handleInput();} };
  const openHTML  = ()     => { setHtmlVal(editorRef.current.innerHTML); setShowHTML(true); };
  const applyHTML = ()     => { editorRef.current.innerHTML = htmlVal; handleInput(); setShowHTML(false); };

  // Image click to select
  const handleEditorClick = useCallback((e) => {
    if (e.target.tagName === 'IMG') {
      setSelImg(e.target);
      setImgSize({ w: e.target.offsetWidth, h: e.target.offsetHeight });
    } else {
      setSelImg(null);
    }
  }, []);

  // Apply width change
  const applyWidth = useCallback((w) => {
    if (!selImg) return;
    selImg.style.width = w + 'px';
    selImg.style.height = 'auto';
    setImgSize({ w, h: selImg.offsetHeight });
    handleInput();
  }, [selImg, handleInput]);

  // Alignment
  const alignImg = useCallback((align) => {
    if (!selImg) return;
    if (align === 'left')   { selImg.style.float='left';  selImg.style.margin='0 16px 8px 0'; selImg.style.display='block'; }
    if (align === 'right')  { selImg.style.float='right'; selImg.style.margin='0 0 8px 16px'; selImg.style.display='block'; }
    if (align === 'center') { selImg.style.float='none';  selImg.style.margin='12px auto';    selImg.style.display='block'; }
    if (align === 'none')   { selImg.style.float='none';  selImg.style.margin='8px 0';        selImg.style.display='block'; }
    handleInput();
  }, [selImg, handleInput]);

  const removeImg = useCallback(() => {
    if (!selImg) return;
    selImg.remove();
    setSelImg(null);
    handleInput();
  }, [selImg, handleInput]);

  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files[0]; if (!file) return; e.target.value='';
    const btn = document.getElementById('rich-img-btn');
    if (btn) { btn.textContent='⏳'; btn.disabled=true; }
    try {
      const fd = new FormData(); fd.append('image', file);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res  = await fetch('/api/admin/upload-image', { method:'POST', headers:token?{Authorization:`Bearer ${token}`}:{}, body:fd });
      const data = await res.json();
      if (data.success && data.url) {
        editorRef.current.focus();
        document.execCommand('insertImage', false, data.url);
        // Style the inserted image
        const imgs = editorRef.current.querySelectorAll('img');
        const last = imgs[imgs.length-1];
        if (last) { last.style.maxWidth='100%'; last.style.borderRadius='8px'; last.style.margin='8px 0'; last.style.cursor='pointer'; }
        handleInput();
      } else alert('Upload failed: '+(data.message||'Unknown error'));
    } catch { alert('Image upload failed.'); }
    finally { if(btn){btn.innerHTML='🖼';btn.disabled=false;} }
  }, [handleInput]);

  const B = ({label, action, title, blue, active}) => (
    <button type="button" title={title}
      style={{ padding:'4px 9px', border:blue?'1px solid #0066ff':'none',
        background:blue?'rgba(0,102,255,0.08)':active?'rgba(0,102,255,0.1)':'transparent',
        cursor:'pointer', fontSize:blue?'0.75rem':'0.82rem',
        color:blue?'#0066ff':active?'#0066ff':'#334155',
        borderRadius:4, fontFamily:'inherit', lineHeight:1.4, fontWeight:blue||active?700:400 }}
      onMouseDown={e=>{e.preventDefault();action();}}
      dangerouslySetInnerHTML={{__html:label}}
    />
  );
  const Sep = () => <div style={{width:1,height:20,background:'#e2e8f0',margin:'0 2px'}}/>;

  return (
    <>
      <div style={{border:'1px solid #e2e8f0',borderRadius:8,overflow:'hidden',background:'#fff'}}>
        <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleImageUpload}/>

        {/* Main toolbar */}
        <div style={{display:'flex',flexWrap:'wrap',gap:2,padding:'6px 8px',background:'#f8fafc',borderBottom:'1px solid #e2e8f0',alignItems:'center'}}>
          <B label="<b>B</b>"     action={()=>exec('bold')}                title="Bold"/>
          <B label="<i>I</i>"     action={()=>exec('italic')}              title="Italic"/>
          <B label="<u>U</u>"     action={()=>exec('underline')}           title="Underline"/>
          <B label="<s>S</s>"     action={()=>exec('strikeThrough')}       title="Strikethrough"/>
          <Sep/>
          <B label="H1"           action={()=>execBlock('h1')}             title="Heading 1"/>
          <B label="H2"           action={()=>execBlock('h2')}             title="Heading 2"/>
          <B label="H3"           action={()=>execBlock('h3')}             title="Heading 3"/>
          <Sep/>
          <B label="&#8226; List" action={()=>exec('insertUnorderedList')} title="Bullet List"/>
          <B label="1. List"      action={()=>exec('insertOrderedList')}   title="Numbered List"/>
          <Sep/>
          <B label="&#8594;"      action={()=>exec('indent')}              title="Indent"/>
          <B label="&#8592;"      action={()=>exec('outdent')}             title="Outdent"/>
          <Sep/>
          <B label="&#128279;"    action={()=>execLink()}                  title="Insert Link"/>
          <button id="rich-img-btn" type="button" title="Insert Image"
            style={{padding:'4px 9px',border:'none',background:'transparent',cursor:'pointer',fontSize:'0.82rem',color:'#334155',borderRadius:4}}
            onMouseDown={e=>{e.preventDefault();fileRef.current.click();}}>🖼</button>
          <Sep/>
          <B label="&#10005;"     action={()=>exec('removeFormat')}        title="Clear Formatting"/>
          <Sep/>
          <B label="&lt;/&gt; HTML" action={openHTML} title="Edit Raw HTML" blue={true}/>
        </div>

        {/* Image toolbar — shows only when image is selected */}
        {selImg && (
          <div style={{display:'flex',flexWrap:'wrap',gap:6,padding:'6px 10px',background:'#eff6ff',borderBottom:'1px solid #bfdbfe',alignItems:'center',fontSize:'0.8rem'}}>
            <span style={{color:'#1d4ed8',fontWeight:600,fontSize:'0.75rem'}}>🖼 Image selected:</span>
            <Sep/>
            <span style={{color:'#475569',fontSize:'0.75rem'}}>Width:</span>
            <input type="number" value={Math.round(imgSize.w)} min={50} max={1200}
              onChange={e=>{ setImgSize(s=>({...s,w:parseInt(e.target.value)||s.w})); }}
              onBlur={e=>applyWidth(parseInt(e.target.value)||imgSize.w)}
              onKeyDown={e=>{ if(e.key==='Enter') applyWidth(parseInt(e.target.value)||imgSize.w); }}
              style={{width:60,padding:'2px 6px',border:'1px solid #e2e8f0',borderRadius:4,fontSize:'0.8rem',color:'#060d1a'}}
            />
            <span style={{color:'#475569',fontSize:'0.75rem'}}>px</span>
            <Sep/>
            <span style={{color:'#475569',fontSize:'0.75rem'}}>Size:</span>
            {[['25%',0.25],['50%',0.5],['75%',0.75],['100%',1]].map(([label,pct])=>(
              <button key={label} type="button" onMouseDown={e=>{e.preventDefault();applyWidth(Math.round(editorRef.current.offsetWidth*pct));}}
                style={{padding:'2px 7px',border:'1px solid #e2e8f0',borderRadius:4,background:'#fff',cursor:'pointer',fontSize:'0.75rem',color:'#334155'}}>
                {label}
              </button>
            ))}
            <Sep/>
            <span style={{color:'#475569',fontSize:'0.75rem'}}>Align:</span>
            {[['◀ Left','left'],['■ Center','center'],['▶ Right','right']].map(([label,align])=>(
              <button key={align} type="button" onMouseDown={e=>{e.preventDefault();alignImg(align);}}
                style={{padding:'2px 7px',border:'1px solid #e2e8f0',borderRadius:4,background:'#fff',cursor:'pointer',fontSize:'0.75rem',color:'#334155'}}>
                {label}
              </button>
            ))}
            <Sep/>
            <button type="button" onMouseDown={e=>{e.preventDefault();removeImg();}}
              style={{padding:'2px 8px',border:'1px solid #fca5a5',borderRadius:4,background:'#fef2f2',cursor:'pointer',fontSize:'0.75rem',color:'#dc2626',fontWeight:600}}>
              🗑 Remove
            </button>
          </div>
        )}

        {/* Editable area */}
        <div ref={editorRef} contentEditable suppressContentEditableWarning
          onInput={handleInput} onBlur={handleInput} onKeyUp={handleInput}
          onMouseUp={handleInput} onPaste={handleInput} onCut={handleInput}
          onClick={handleEditorClick}
          data-placeholder={placeholder}
          style={{minHeight:220,padding:'12px 16px',fontSize:'0.95rem',lineHeight:1.8,color:'#060d1a',WebkitTextFillColor:'#060d1a',outline:'none',background:'#fff'}}
        />

        <style>{`
          [contenteditable]:empty:before{content:attr(data-placeholder);color:#94a3b8;pointer-events:none;}
          [contenteditable]{color:#060d1a !important;-webkit-text-fill-color:#060d1a !important;background:#fff !important;}
          [contenteditable] *{color:#060d1a !important;-webkit-text-fill-color:#060d1a !important;}
          [contenteditable] h1{font-size:1.4rem;font-weight:700;margin:0.75rem 0 0.4rem;}
          [contenteditable] h2{font-size:1.2rem;font-weight:700;margin:0.75rem 0 0.4rem;}
          [contenteditable] h3{font-size:1.05rem;font-weight:700;margin:0.6rem 0 0.3rem;}
          [contenteditable] ul,[contenteditable] ol{margin:0.4rem 0 0.4rem 1.4rem;}
          [contenteditable] li{margin-bottom:0.2rem;}
          [contenteditable] a{color:#0066ff !important;-webkit-text-fill-color:#0066ff !important;}
          [contenteditable] img{max-width:100%;border-radius:8px;margin:8px 0;cursor:pointer;}
          [contenteditable] img.selected{outline:3px solid #0066ff;outline-offset:2px;border-radius:8px;}
          [contenteditable] blockquote{border-left:4px solid #0066ff;margin:0.75rem 0;padding:0.5rem 1rem;background:rgba(0,102,255,0.04);}
        `}</style>
      </div>

      {/* HTML Modal */}
      {showHTML && (
        <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}}>
          <div style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:800,boxShadow:'0 24px 60px rgba(0,0,0,0.3)',overflow:'hidden'}}>
            <div style={{padding:'1rem 1.5rem',borderBottom:'1px solid #e2e8f0',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <h6 style={{margin:0,fontFamily:'Space Grotesk,system-ui,sans-serif',color:'#060d1a',fontSize:'1rem'}}>Edit / Paste HTML</h6>
              <button type="button" onClick={()=>setShowHTML(false)} style={{border:'none',background:'none',fontSize:'1.2rem',cursor:'pointer',color:'#64748b'}}>✕</button>
            </div>
            <div style={{padding:'1rem 1.5rem'}}>
              <p style={{fontSize:'0.82rem',color:'#64748b',marginBottom:'0.75rem'}}>Paste your HTML below then click <strong>Apply</strong>.</p>
              <textarea value={htmlVal} onChange={e=>setHtmlVal(e.target.value)}
                style={{width:'100%',height:360,padding:'0.75rem',border:'1px solid #e2e8f0',borderRadius:8,fontSize:'0.82rem',fontFamily:'monospace',color:'#060d1a',background:'#f8fafc',resize:'vertical',outline:'none',lineHeight:1.6}}
                spellCheck={false} autoFocus/>
            </div>
            <div style={{padding:'0.75rem 1.5rem',borderTop:'1px solid #e2e8f0',display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button type="button" onClick={()=>setShowHTML(false)}
                style={{padding:'0.6rem 1.5rem',border:'1px solid #e2e8f0',borderRadius:8,background:'#fff',color:'#64748b',cursor:'pointer',fontSize:'0.9rem'}}>Cancel</button>
              <button type="button" onClick={applyHTML}
                style={{padding:'0.6rem 1.5rem',border:'none',borderRadius:8,background:'linear-gradient(135deg,#0066ff,#00d4ff)',color:'#fff',cursor:'pointer',fontSize:'0.9rem',fontWeight:700}}>Apply HTML</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
